import { Constants } from "appConstants";
import { FieldsSection } from "pages/ApplicationDetail/modules/ApplicationDetailData";
import { ApplicationData, ApplicationProps } from "pages/NewApplication/steps/types";
import { ApplicationFormDocumentFolder } from "services/types";
import { Applicant } from "./Applicant";
import { UserAuthorities } from "./auth/types";
import { BaseModel } from "./BaseModel";
import { Concessionaire } from "./Concessionaire";
import { ExemptionType } from "./ExemptionType";
import { RegulatoryAgency } from "./RegulatoryAgency";
import { ApplicationFinalStatus, ApplicationStatus, ExemptionTypeData } from "./types";
import { Vehicle } from "./Vehicle";

export class Application extends BaseModel<ApplicationData> {
	id: string;
	concessionaire: Concessionaire;
	regulatoryAgency: RegulatoryAgency;
	applicationProcessCode: string;
	exemptionType: ExemptionTypeData;
	status: ApplicationStatus | string;
	currentUserResponsibleEmail: string | undefined;
	currentUserResponsibleGroup: Constants.UserRoleGroups | undefined;
	currentResponsibleUserName: string | undefined;
	documentFolders: ApplicationFormDocumentFolder[];
	applicantData: Applicant;
	applicationVehicle: Vehicle;
	issuedAt: string;
	exemptionTypeName: string;
	highwayName: string;
	highwayCode: string;
	concessionaireName: string;
	regulatoryAgencyName: string;
	analysisLimitDatetime: string;

	constructor(data: ApplicationProps) {
		super(data);
		this.id = data.id ?? "";
		this.status = data.status ?? ApplicationStatus.RASCUNHO;
		this.applicationProcessCode = data.applicationProcessCode ?? "";
		this.issuedAt = data.issuedAt ?? "";
		this.exemptionTypeName = data.exemptionTypeName ?? "";
		this.highwayName = data.highwayName ?? "";
		this.highwayCode = data.highwayCode ?? "";
		this.concessionaireName = data.concessionaireName ?? "";
		this.documentFolders = data.documentFolders ? Object.values(data.documentFolders) : [];
		this.applicantData = Applicant.getValueOrNew(data.applicantData);
		this.applicationVehicle = Vehicle.getValueOrNew(data.applicationVehicle);
		this.exemptionType = ExemptionType.getValueOrNew(data.exemptionType ?? {});
		this.concessionaire = Concessionaire.getValueOrNew(data.concessionaire ?? {});
		this.regulatoryAgency = RegulatoryAgency.getValueOrNew(data.regulatoryAgency ?? {});
		this.currentUserResponsibleEmail = data.currentUserResponsibleEmail;
		this.currentUserResponsibleGroup = data.currentUserResponsibleGroup;
		this.currentResponsibleUserName = data.currentResponsibleUserName;
		this.regulatoryAgencyName = data.regulatoryAgencyName ?? "";
		this.analysisLimitDatetime = data.analysisLimitDatetime ?? "";
	}

	static createFromResponse(response: ApplicationProps) {
		if (!response) {
			return response;
		}

		const exemptionTypeName =
			response.exemptionTypeName || (typeof response.exemptionType === "string" ? response.exemptionType : response.exemptionType?.name ?? "");
		const concessionaireName = response.concessionaireName || (response.concessionaire ? response.concessionaire.name : "");

		return new this({
			...response,
			exemptionTypeName,
			concessionaireName
		});
	}

	isCurrentUserResponsible(userEmail: string | undefined) {
		if (!userEmail) return false;
		return this.currentUserResponsibleEmail === userEmail;
	}

	isCurrentUserInResponsibleGroup(authorities: UserAuthorities[]): boolean {
		const responsibleGroup = this.currentUserResponsibleGroup ?? Constants.UserRoleGroups.SOLICITANTE;
		return !!authorities.find((ug) => ug === UserAuthorities.ADMINISTRADOR || ug.replace("ROLE_", "") === responsibleGroup);
	}

	hasUserResponsible() {
		return !!this.currentUserResponsibleEmail;
	}

	isInFinalStatus() {
		return Application.isInFinalStatus(this.status as ApplicationStatus);
	}

	static isInFinalStatus(status: ApplicationStatus) {
		return ApplicationFinalStatus.includes(status);
	}

	isInBOReadOnlyStatus() {
		const boReadOnlyStatus = [
			ApplicationStatus.APROVADO,
			ApplicationStatus.CANCELADO,
			ApplicationStatus.SOLICITANTE_PENDENTE,
			ApplicationStatus.REPROVADO
		];
		return boReadOnlyStatus.includes(this.status as ApplicationStatus);
	}

	get applicationDetails(): FieldsSection[] {
		return [
			{
				title: "",
				nameValueFields: [
					{
						name: "Concessionária",
						value: this.concessionaire.name
					},
					{
						name: "Tipo de isenção",
						value: this.exemptionTypeName
					}
				]
			}
		];
	}

	get applicantDetails(): FieldsSection[] {
		return [
			{
				title: "",
				nameValueFields: [
					{
						name: "Perfil",
						value: this.applicantData?.applicantTypeLabel
					},
					{
						name: this.applicantData?.isPJ ? "Razão social" : "Nome completo",
						value: this.applicantData?.applicantName
					},
					{
						name: this.applicantData?.applicantDocumentType,
						value: this.applicantData?.applicantDocument
					}
				]
			},
			{
				title: "Endereço cadastral",
				nameValueFields: [
					{
						name: "CEP",
						value: this.applicantData?.applicantAddressPostalCode
					},
					{
						name: "Estado",
						value: this.applicantData?.applicantAddressState
					},
					{
						name: "Cidade",
						value: this.applicantData?.applicantAddressCity
					},
					{
						name: "Bairro",
						value: this.applicantData?.applicantAddressNeighbourhood
					},
					{
						name: "Endereço",
						value: this.applicantData?.applicantAddressStreet
					},
					{
						name: "Número",
						value: this.applicantData?.applicantAddressNumber
					},
					{
						name: "Complemento",
						value: this.applicantData?.applicantAddressComplement,
						condition: !!this.applicantData?.applicantAddressComplement
					}
				]
			},
			{
				title: "Contato direto para tratar a solicitação",
				nameValueFields: [
					{
						name: "Nome",
						value: this.applicantData?.applicantName
					},
					{
						name: "Telefone",
						value: this.applicantData?.applicantPhoneNumber
					},
					{
						name: "E-mail",
						value: this.applicantData?.applicantEmail
					}
				]
			}
		];
	}
}
